import { T } from './Translate';
import React, { useState, useEffect, useCallback } from 'react';
import { Switch, List, ListItem, ListItemIcon, ListItemText, Typography, Divider, Snackbar, CircularProgress, Container, Toolbar, AppBar, Box } from '@mui/material';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import Atras from './BackButton';
import { pageContentSx } from '../pageLayout';

const SettingNotification = () => {
  const [permissions, setPermissions] = useState({
    notifications: false,
    geolocation: 'prompt', // 'granted', 'prompt', 'denied'
  });
  const [locationEnabled, setLocationEnabled] = useState(true); // user preference
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [loading, setLoading] = useState(true);

  const checkPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const notificationStatus = await navigator.permissions.query({ name: 'notifications' });
      const geolocationStatus = await navigator.permissions.query({ name: 'geolocation' });

      setPermissions({
        notifications: notificationStatus.state === 'granted',
        geolocation: geolocationStatus.state, // 'granted', 'prompt', 'denied'
      });
    } catch (error) {
      console.error('Error checking permissions:', error);
      setPermissions({
        notifications: Notification.permission === 'granted',
        geolocation: 'prompt',
      });
    } finally {
      // Load user preference from Firestore if available
      try {
        const user = auth.currentUser;
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await (await import('firebase/firestore')).getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (typeof data.locationEnabled === 'boolean') {
              setLocationEnabled(data.locationEnabled);
            }
          }
        }
      } catch (e) {}
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkPermissions();

    const permissionChangeHandler = () => {
      checkPermissions();
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
        permissionStatus.onchange = permissionChangeHandler;
      });
      navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
        permissionStatus.onchange = permissionChangeHandler;
      });
    }

    // Add window focus event to re-check permissions
    window.addEventListener('focus', checkPermissions);

    return () => {
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
          permissionStatus.onchange = null;
        });
        navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
          permissionStatus.onchange = null;
        });
      }
      window.removeEventListener('focus', checkPermissions);
    };
  }, [checkPermissions]);

  const updateUserPreference = async (setting, value) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { [setting]: value });
      }
    } catch (error) {
      console.error(`Error updating ${setting} preference:`, error);
    }
  };

  const handleNotificationsChange = async (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        updateUserPreference('notificationsEnabled', true);
        showSnackbar(<T>Notificaciones activadas.</T>);
      } else {
        showSnackbar(<T>Permiso de notificaciones denegado.</T>);
      }
    } else {
      updateUserPreference('notificationsEnabled', false);
      showSnackbar(<T>Para desactivar las notificaciones, debes hacerlo desde la configuración de tu navegador.</T>, 6000);
    }
    checkPermissions();
  };

  const handleLocationChange = async (event) => {
    const isChecked = event.target.checked;
    if (permissions.geolocation === 'denied') {
      showSnackbar(<T>Para desactivar la ubicación, debes hacerlo desde la configuración de tu navegador.</T>, 6000);
      return;
    }
    setLocationEnabled(isChecked);
    updateUserPreference('locationEnabled', isChecked);
    showSnackbar(isChecked ? <T>Ubicación activada.</T> : <T>Ubicación desactivada.</T>);
  };

  const showSnackbar = (message, duration = 3000) => {
    setSnackbar({ open: true, message, duration });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ bgcolor: '#312783' }}>
        <Toolbar
          sx={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: { xs: '100%', md: '980px' },
              mx: 'auto',
            }}
          >
            <Atras color="inherit" />
            <Typography variant="h5" color={'white'} sx={{ ml: 2 }} gutterBottom>
              <T>Configuración</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 16, mb: 16, textAlign: 'center' }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <List>
            <ListItem>
              <ListItemIcon>
                <Typography color={"black"}><T>Notificaciones</T></Typography>
              </ListItemIcon>
              <ListItemText/>
              <Switch
                checked={permissions.notifications}
                onChange={handleNotificationsChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Typography color={"black"}><T>Ubicación</T></Typography>
              </ListItemIcon>
              <ListItemText/>
              <Switch
                checked={permissions.geolocation === 'denied' ? false : locationEnabled}
                onChange={handleLocationChange}
                inputProps={{ 'aria-label': 'controlled' }}
                disabled={permissions.geolocation === 'denied'}
              />
            </ListItem>
            <Divider />
          </List>
        )}
      </Container>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Container>
  );
};

export default SettingNotification;
